import {
    INDEMNITY_MAX_LENGTHS,
    RECOMMENDATION_MAX_LENGTH,
    RECOMMENDATION_MIN_LENGTH,
    type IndemnityPrefill,
    type MeansOfIdentification,
    type SubmitGuarantorIndemnityRequest,
} from '@/types';

/**
 * Local form state.
 *
 * Deliberately a different type from the request body: it holds strings for
 * every text field (so inputs stay controlled before anything is typed) and
 * allows an empty `meansOfIdentification`. `toRequest` narrows it to the exact
 * sixteen keys the API accepts.
 */
export interface IndemnityFormState {
    fullName: string;
    residentialAddress: string;
    phoneNumber: string;
    email: string;
    occupation: string;
    employerBusinessName: string;
    officeAddress: string;
    relationshipWithApplicant: string;
    periodKnown: string;
    meansOfIdentification: MeansOfIdentification | '';
    identificationNumber: string;
    indemnityAccepted: boolean;
    verificationConsentAccepted: boolean;
    recommendation: string;
    declarationAccepted: boolean;
    signatureName: string;
}

export type IndemnityFieldErrors = Partial<Record<keyof IndemnityFormState, string>>;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Seeds the form from the API prefill; nulls become empty strings. */
export const initialFormState = (prefill: IndemnityPrefill): IndemnityFormState => ({
    fullName: prefill.fullName ?? '',
    residentialAddress: prefill.residentialAddress ?? '',
    phoneNumber: prefill.phoneNumber ?? '',
    email: prefill.email ?? '',
    occupation: prefill.occupation ?? '',
    employerBusinessName: prefill.employerBusinessName ?? '',
    officeAddress: prefill.officeAddress ?? '',
    relationshipWithApplicant: prefill.relationshipWithApplicant ?? '',
    periodKnown: prefill.periodKnown ?? '',
    meansOfIdentification: prefill.meansOfIdentification ?? '',
    identificationNumber: prefill.identificationNumber ?? '',
    indemnityAccepted: false,
    verificationConsentAccepted: false,
    recommendation: prefill.recommendation ?? '',
    declarationAccepted: false,
    signatureName: '',
});

/**
 * The server compares the typed signature to `fullName` ignoring case and
 * extra whitespace only. Mirrored exactly so a completed form can't fail on its
 * last step.
 */
export const normaliseForSignature = (value: string): string => value.trim().replace(/\s+/g, ' ').toLowerCase();

export const signatureMatches = (signatureName: string, fullName: string): boolean =>
    normaliseForSignature(signatureName) !== '' &&
    normaliseForSignature(signatureName) === normaliseForSignature(fullName);

const TEXT_FIELDS: { key: keyof typeof INDEMNITY_MAX_LENGTHS; label: string }[] = [
    { key: 'fullName', label: 'Full name' },
    { key: 'residentialAddress', label: 'Residential address' },
    { key: 'phoneNumber', label: 'Phone number' },
    { key: 'email', label: 'Email address' },
    { key: 'occupation', label: 'Occupation' },
    { key: 'employerBusinessName', label: 'Employer / business name' },
    { key: 'officeAddress', label: 'Office address' },
    { key: 'relationshipWithApplicant', label: 'Relationship with the applicant' },
    { key: 'periodKnown', label: 'Period known' },
    { key: 'identificationNumber', label: 'Identification number' },
];

/** Mirrors the server rules so the guarantor isn't bounced after submitting. */
export const validate = (state: IndemnityFormState): IndemnityFieldErrors => {
    const errors: IndemnityFieldErrors = {};

    for (const { key, label } of TEXT_FIELDS) {
        const value = state[key].trim();
        if (!value) {
            errors[key] = `${label} is required.`;
        } else if (value.length > INDEMNITY_MAX_LENGTHS[key]) {
            errors[key] = `${label} must be ${INDEMNITY_MAX_LENGTHS[key]} characters or fewer.`;
        }
    }

    if (!errors.email && !EMAIL_PATTERN.test(state.email.trim())) {
        errors.email = 'Enter a valid email address.';
    }

    if (!state.meansOfIdentification) {
        errors.meansOfIdentification = 'Select a means of identification.';
    }

    const recommendation = state.recommendation.trim();
    if (recommendation.length < RECOMMENDATION_MIN_LENGTH) {
        errors.recommendation = `Please write at least ${RECOMMENDATION_MIN_LENGTH} characters.`;
    } else if (recommendation.length > RECOMMENDATION_MAX_LENGTH) {
        errors.recommendation = `Please keep this under ${RECOMMENDATION_MAX_LENGTH} characters.`;
    }

    if (!state.signatureName.trim()) {
        errors.signatureName = 'Type your full name to sign.';
    } else if (!signatureMatches(state.signatureName, state.fullName)) {
        errors.signatureName = 'This must match the full name given above.';
    }

    return errors;
};

/** All three acceptances ticked — gates the submit button. */
export const allAccepted = (state: IndemnityFormState): boolean =>
    state.indemnityAccepted && state.verificationConsentAccepted && state.declarationAccepted;

/**
 * Narrows form state to exactly the sixteen keys the API accepts.
 *
 * Built key-by-key on purpose. The endpoint runs `forbidNonWhitelisted`, so
 * spreading state — and picking up any UI-only field added later — turns into a
 * 400 naming the offending property.
 */
export const toRequest = (state: IndemnityFormState): SubmitGuarantorIndemnityRequest => ({
    fullName: state.fullName.trim(),
    residentialAddress: state.residentialAddress.trim(),
    phoneNumber: state.phoneNumber.trim(),
    email: state.email.trim(),
    occupation: state.occupation.trim(),
    employerBusinessName: state.employerBusinessName.trim(),
    officeAddress: state.officeAddress.trim(),
    relationshipWithApplicant: state.relationshipWithApplicant.trim(),
    periodKnown: state.periodKnown.trim(),
    meansOfIdentification: state.meansOfIdentification as MeansOfIdentification,
    identificationNumber: state.identificationNumber.trim(),
    indemnityAccepted: state.indemnityAccepted,
    verificationConsentAccepted: state.verificationConsentAccepted,
    recommendation: state.recommendation.trim(),
    declarationAccepted: state.declarationAccepted,
    signatureName: state.signatureName.trim(),
});

/** Field a server-side message should be surfaced against, where identifiable. */
export const fieldForServerMessage = (message: string): keyof IndemnityFormState | null => {
    const m = message.toLowerCase();
    if (m.includes('signature')) return 'signatureName';
    if (m.includes('recommendation')) return 'recommendation';
    if (m.includes('email')) return 'email';
    if (m.includes('identification number')) return 'identificationNumber';
    if (m.includes('means of identification')) return 'meansOfIdentification';
    if (m.includes('phone')) return 'phoneNumber';
    return null;
};
